import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import type { AuthResponse, AuthUser } from "@meupedaco/shared";
import { createHttpClient } from "@meupedaco/shared";

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
  client: ReturnType<typeof createHttpClient>;
};

const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) {
  throw new Error("VITE_API_URL is required.");
}
const ACCESS_TOKEN_KEY = "mpf_admin_access_token";
const REFRESH_TOKEN_KEY = "mpf_admin_refresh_token";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const clearSession = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);

    // Evitar loop de redirecionamento
    const currentPath = window.location.pathname;
    if (currentPath !== "/login" && currentPath !== "/forgot-password" && currentPath !== "/reset-password") {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const setSession = useCallback((data: AuthResponse) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    setUser(data.user);
  }, []);

  const updateUser = useCallback((nextUser: AuthUser) => {
    setUser(nextUser);
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Não chamar clearSession aqui, apenas retornar null
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setUser(null);
        return null;
      }

      const data = (await response.json()) as AuthResponse;
      setSession(data);
      return data.accessToken;
    } catch (error) {
      console.error("Refresh error:", error);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
      return null;
    }
  }, [setSession]);

  const client = useMemo(
    () =>
      createHttpClient({
        baseUrl: API_URL,
        getAccessToken,
        setAccessToken: (token) => {
          if (token) {
            localStorage.setItem(ACCESS_TOKEN_KEY, token);
          } else {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
          }
        },
        refresh,
        onUnauthorized: () => {
          // Limpar sessão sem redirecionar (será feito pelo RequireAuth)
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setUser(null);
        },
      }),
    [refresh]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = (await response.json()) as AuthResponse;
      setSession(data);
    },
    [setSession]
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await client.post("/auth/logout", { refreshToken });
    }
    clearSession();
  }, [clearSession, client]);

  useEffect(() => {
    const init = async () => {
      await refresh();
      setLoading(false);
    };
    init();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, login, logout, updateUser, client }),
    [user, loading, login, logout, updateUser, client]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
